<template>
  <component
    :is="element"
    class="button"
    :class="[
      $blockClass(blockClass, 'button'),
      modifierClasses,
      {
        [$class('active')]: showStateIcon,
        'button--has-hover-icon': $slots.hoverIcon,
      },
    ]"
    :href="url || null"
    :type="type || null"
    :aria-label="iconOnly ? label : null"
  >
    <span
      v-if="!iconOnly"
      class="button__label"
      :class="[
        $blockClass(blockClass, 'button-label'),
        { 'button__label--no-icon': !showIcon },
      ]"
      v-text="label"
    />

    <span
      v-if="showIcon"
      class="button__icon"
      :class="$blockClass(blockClass, 'button-icon')"
    >
      <slot name="icon">
        <icon-filters v-if="icon === 'filters'" />
        <icon-arrow-right v-else />
      </slot>

      <span
        v-if="$slots.hoverIcon"
        class="button__hover-icon"
        :class="$blockClass(blockClass, 'button-hover-icon')"
      >
        <slot name="hoverIcon" />
      </span>
    </span>

    <span
      v-if="showStateIcon && $slots.stateIcon"
      class="button__state"
      :class="$blockClass(blockClass, 'button-state')"
    >
      <slot name="stateIcon" />
    </span>
  </component>
</template>

<script>

/**
 * Vue: Button (btn)
 * -----------------------------------------------------------------------------
 * Global interactive button element.
 * - Styles are imported in critical styles.
 * - Must be imported as <btn> as <button> is a default element.
 *
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {String} [customElement] - Optional override to set button element.
 * @param {String} label - Button label.
 * @param {String} [modifiers] - Button modifiers in space separated list
 * @param {Boolean} iconOnly - Prevent rendering of label.
 * (secondary, light, outlined, text, block, reversed, or large).
 * @param {Boolean} [showIcon] - Whether to show the button icon.
 * @param {Boolean} [showStateIcon] - Show secondary button state, replaces text
 * and default icon with `stateIcon` slot.
 * @param {String} [type] - Type of button (button or submit).
 * @param {String} [url] - URL if link.
 *
 */
import IconArrowRight from '~icons/direction/arrow/right.svg'
import IconFilters from '~icons/misc/filters.svg'

export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Btn',

  components: {
    IconArrowRight,
    IconFilters,
  },

  props: {
    blockClass: {
      type: String,
      default: '',
    },
    customElement: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
      required: true,
    },
    modifiers: {
      type: [String, Array],
      default: '',
    },
    icon: {
      type: String,
      default: '',
    },
    iconOnly: {
      type: Boolean,
      default: false,
    },
    showIcon: {
      type: Boolean,
      default: true,
    },
    showStateIcon: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: '',
      validator: (value) => {
        return ['', 'button', 'submit'].includes(value)
      },
    },
    url: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      cnvsGlobal: true,
    }
  },

  computed: {

    /**
     * Compute element type.
     * @returns {String}
     */
    element() {
      if (this.customElement) {
        return this.customElement
      }

      return this.url ? 'a' : 'button'
    },

    /**
     * Compute modifier classes.
     * @returns {String}
     */
    modifierClasses() {
      if (!this.modifiers) {
        return ''
      }

      let modifiers = []

      if (Array.isArray(this.modifiers)) {
        modifiers = this.modifiers
      } else {
        modifiers = this.modifiers.split(/[\s,]/g)
      }

      return modifiers.map((modifier) => {
        return `button--${modifier}`
      }).join(' ')
    },
  },
}
</script>
